/**
 * VoiceRecorder Component
 *
 * Two distinct button states for recording audio:
 *   Idle        → mic icon, click to start recording
 *   Recording   → square stop icon with pulsing red ring, click to stop
 *   Transcribing → spinner while Whisper processes the audio
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Button, Tooltip } from 'antd';
import { AudioOutlined, LoadingOutlined } from '@ant-design/icons';

interface VoiceRecorderProps {
  onTranscription: (text: string) => void;
  transcribeAudio: (blob: Blob) => Promise<string>;
  isTranscribing: boolean;
  disabled?: boolean;
}

const StopIcon: React.FC = () => (
  <span
    style={{
      display: 'inline-block',
      width: 12,
      height: 12,
      borderRadius: 2,
      backgroundColor: '#ff4d4f',
    }}
  />
);

const VoiceRecorder: React.FC<VoiceRecorderProps> = ({
  onTranscription,
  transcribeAudio,
  isTranscribing,
  disabled,
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (isRecording) {
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed(s => s + 1), 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
      setElapsed(0);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [isRecording]);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';

      const recorder = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: mimeType });
        if (blob.size > 0) {
          try {
            const text = await transcribeAudio(blob);
            if (text.trim()) onTranscription(text);
          } catch (err) {
            console.error('Transcription failed:', err);
          }
        }
      };

      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
    } catch (err) {
      console.error('Microphone access denied:', err);
    }
  }, [transcribeAudio, onTranscription]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  }, []);

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  if (isTranscribing) {
    return (
      <Tooltip title="Transcribing...">
        <Button
          type="text"
          icon={<LoadingOutlined spin />}
          disabled
        />
      </Tooltip>
    );
  }

  if (isRecording) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 12, color: '#ff4d4f', fontWeight: 500, minWidth: 32 }}>
          {formatTime(elapsed)}
        </span>
        <Tooltip title="Stop recording">
          <Button
            type="text"
            icon={<StopIcon />}
            onClick={stopRecording}
            style={{
              border: '2px solid #ff4d4f',
              borderRadius: '50%',
              width: 32,
              height: 32,
              minWidth: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: 'pulse-ring 1.5s infinite',
            }}
          />
        </Tooltip>
        <style>{`
          @keyframes pulse-ring {
            0% { box-shadow: 0 0 0 0 rgba(255, 77, 79, 0.4); }
            70% { box-shadow: 0 0 0 6px rgba(255, 77, 79, 0); }
            100% { box-shadow: 0 0 0 0 rgba(255, 77, 79, 0); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <Tooltip title="Voice input">
      <Button
        type="text"
        icon={<AudioOutlined />}
        onClick={startRecording}
        disabled={disabled}
      />
    </Tooltip>
  );
};

export default VoiceRecorder;
